import React, { ChangeEvent, Fragment, useMemo } from "react";

import { Divider, FormLabel, IconButton, Radio, RadioGroup, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import clsx from "clsx";

import makeStyles from "hooks/makeStyles";
import useMenus from "hooks/useMenus";

const useStyles = makeStyles(theme => ({
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start"
	},
	closeIcon: {
		width: 16,
		height: 16,
		color: "#fff"
	},
	title: {
		alignSelf: "flex-start",
		textAlign: "left",
		textTransform: "uppercase",
		marginTop: theme.spacing(2),
		fontWeight: 500,
		fontSize: 16,
		lineHeight: 1,
		letterSpacing: "0.38px"
	},
	items: {
		marginBottom: theme.spacing(2)
	},
	itemDivider: {
		marginTop: theme.spacing(2),
		backgroundColor: "#f2f2f2"
	},
	item: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	itemTitle: {
		color: "#f2f2f2",
		textTransform: "uppercase",
		fontSize: "12px",
		letterSpacing: "0.38px"
	},
	itemTitleChecked: {
		color: "#fff"
	},
	radioIcon: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #f2f2f2"
	},
	radioIconChecked: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #fff",
		backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
		"&:before": {
			display: "block",
			width: 11,
			height: 11,
			backgroundImage: "radial-gradient(#BDBDBD,#BDBDBD 56%,transparent 32%)",
			content: '""'
		}
	}
}));

interface Props {
	selectedMenuSlug: null | string;
	onChange: (orderTypeId: string) => void;
	onClose: () => void;
}

const MenuModal = ({ selectedMenuSlug, onChange, onClose }: Props) => {
	const classes = useStyles();
	const { menus } = useMenus();

	const value = selectedMenuSlug || (menus[0] ? menus[0].id : "");

	const handleChange = useMemo(() => (event: ChangeEvent<HTMLInputElement>, value: string) => onChange(value), [
		onChange
	]);

	return (
		<>
			<IconButton className={classes.closeButton} onClick={onClose}>
				<Close className={classes.closeIcon} />
			</IconButton>

			<Typography className={classes.title}>Menu</Typography>

			<RadioGroup className={classes.items} aria-label="option" value={value} onChange={handleChange}>
				{menus.map(menu => (
					<Fragment key={menu.id}>
						<Divider className={classes.itemDivider} />

						<FormLabel className={classes.item}>
							<Typography className={clsx(classes.itemTitle, { [classes.itemTitleChecked]: value === menu.slug })}>
								{menu.name}
							</Typography>
							<Radio
								inputProps={{ "aria-label": menu.name }}
								icon={<span className={classes.radioIcon} />}
								checkedIcon={<span className={classes.radioIconChecked} />}
								color="secondary"
								value={menu.slug}
							/>
						</FormLabel>
					</Fragment>
				))}
			</RadioGroup>
		</>
	);
};

export default MenuModal;
